import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useCart } from '../context/CartContext';
import { getPendingOrder, clearPendingOrder } from '../lib/pendingOrder';

// Shown on the cart and checkout pages when this browser already started an
// order that never finished.
//
// Without it, the abandoned-checkout path is a trap. The order exists and holds
// the stock, so trying again returns "sold out" — on stock the shopper is
// holding themselves. They cannot tell whether they were charged, whether they
// own the item, or whether to try again. The most likely reaction is to order
// again, which is exactly the double purchase we are trying to avoid.
//
// So this reads the real order rather than guessing from local state, and says
// one specific thing per outcome.

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function PendingOrderBanner() {
  const [pending, setPending] = useState(null);
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();

  const check = useCallback(async () => {
    const p = getPendingOrder();
    if (!p) { setPending(null); setOrder(null); return; }

    const { data, error } = await supabase.rpc('get_order_by_token', { p_token: p.token });
    if (error || !data) {
      // The token is meaningless now — a deleted order, or a token from
      // another environment. Nothing useful to say, so stop nagging.
      clearPendingOrder();
      setPending(null);
      return;
    }

    // Finished orders are not "pending" — drop the marker and empty the cart,
    // which is the point at which clearing it is actually safe.
    if (['authorized', 'paid'].includes(data.payment_status)) {
      clearPendingOrder();
      clearCart();
    }
    // Dead ends release their own stock; the shopper is free to try again.
    if (['expired', 'canceled', 'failed'].includes(data.payment_status)) {
      clearPendingOrder();
    }

    setPending(p);
    setOrder(data);
  }, [clearCart]);

  useEffect(() => { check(); }, [check]);

  if (!pending || !order) return null;

  const dismiss = () => { clearPendingOrder(); setPending(null); setOrder(null); };

  const shell = (tone, icon, title, body, actions) => (
    <div
      style={{
        border: `1px solid var(--${tone})`,
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 20,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <span style={{ color: `var(--${tone})`, flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{title}</div>
        <div style={{ fontSize: '0.86rem', lineHeight: 1.6, color: 'var(--text-muted)', marginTop: 5 }}>
          {body}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>{actions}</div>
      </div>
    </div>
  );

  // Already went through. The danger here is ordering a second time believing
  // the first did not work, so it says plainly that it did.
  if (['authorized', 'paid'].includes(order.payment_status)) {
    return shell('success', <CheckCircle2 size={18} />,
      'You already ordered this — do not order again',
      <>
        Your order for <strong>{money(order.total_cents)}</strong> went through
        {order.payment_status === 'authorized'
          ? ' and your card is on hold. You have not been charged yet; we take payment when we print your shipping label.'
          : ' and has been paid.'}
        {' '}Ordering again would buy a second one.
      </>,
      <Link to={`/order/${pending.token}`} className="sv-btn" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
        View your order
      </Link>);
  }

  // Still live. Send them back to the same Stripe session rather than starting
  // a second order for the same items.
  if (order.payment_status === 'pending') {
    return shell('warning', <Clock size={18} />,
      'You have a checkout in progress',
      <>
        You started an order for <strong>{money(order.total_cents)}</strong> and did not finish paying.
        Those items are being held for you right now, which is why you cannot add them again.
        Finish that checkout rather than starting a new one — starting again could leave you with
        two orders for the same thing. If you leave it, the hold expires by itself and nothing
        is charged.
      </>,
      <>
        {pending.url && (
          <a href={pending.url} className="sv-btn" style={{ padding: '8px 14px', fontSize: '0.85rem', textDecoration: 'none' }}>
            Finish paying
          </a>
        )}
        <Link to={`/order/${pending.token}`} className="sv-btn-outline" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
          See what I ordered
        </Link>
        <button type="button" onClick={dismiss} className="sv-btn-outline" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
          Start over instead
        </button>
      </>);
  }

  // Expired, released, or failed. Nothing was charged and the items are back.
  return shell('warning', <AlertTriangle size={18} />,
    'Your previous checkout expired',
    <>
      You did not finish paying in time, so we released the hold and put your items back.
      <strong> Nothing was charged</strong> — there is no payment to cancel and nothing to refund.
      If you still want them, add them to your cart and check out again.
    </>,
    <button type="button" onClick={dismiss} className="sv-btn" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
      Got it
    </button>);
}
